import Tooltip from '../Tooltip';
import { convertDockerHost, formatTimestamp, getWebUrl } from '../../utils/network/networkUtils';
import styles from './ConnectionInfo.module.css';

/**
 * ConnectionInfo component displays detailed information about a connection
 */
export default function ConnectionInfo({
    connection,
    mode,
    role,
    folderId,
    conflictingRemote,
    approveError,
    errorResponse,
    errorMatchesThisConnection,
    duplicateError,
    existingConnection,
    assetstoreConflict,
    conflictingAssetstore,
    isApprovingWithAssetstore,
    approveConnection
}) {
    const status = connection.status;
    // Format timestamps for display - timestamps are created and saved by the server
    const requestedDisplay = formatTimestamp(connection.created);
    const approvedDisplay = formatTimestamp(connection.approved);
    const canceledDisplay = formatTimestamp(connection.canceled);
    const rejectedDisplay = formatTimestamp(connection.rejected);
    const terminatedDisplay = formatTimestamp(connection.terminated);
    
    // Determine which BDSA info to show based on perspective
    // For hub receiving requests: show remote (spoke) BDSA info and initiator
    // For spoke initiating requests: show remote (hub) BDSA info and initiator (self)
    const displayName = connection.remoteDisplayName || 'Unknown';
    const contactName = connection.remoteContactName || connection.contactName || 'Unknown';
    const contactEmail = connection.remoteContactEmail || connection.contactEmail || 'Unknown';
    const initiatorName = connection.initiatorAdminName || 'Unknown';
    const initiatorEmail = connection.initiatorAdminEmail || 'Unknown';
    const initiatorDisplayName = connection.initiatorDisplayName || 'Unknown';
    
    // Determine if remote is hub or node based on mode and role
    // In hub-spoke mode: if we're a hub, remote is a node (spoke); if we're a spoke, remote is a hub
    const remoteTypeLabel = (mode === 'hub-spoke' && role === 'hub') ? 'Remote node' : 
                            (mode === 'hub-spoke' && role === 'spoke') ? 'Network' : 'Remote node';
    
    // Format "Initiated by" field: "Display Name (first name lastname, email)" - removed URL
    const initiatedByText = initiatorDisplayName 
        ? `${initiatorDisplayName} (${initiatorName}, ${initiatorEmail})`
        : `${initiatorName} (${initiatorEmail})`;
    
    // Homepage URL for the remote BDSA (strips /dsa/api/v1)
    const remoteWebUrl = getWebUrl(connection.url) || convertDockerHost(connection.url || '');

    return (
        <div data-sourcepanel="ConnectionInfo.js">
            <div className={styles.connectionLinksRow}>
                <div className={styles.connectionInfoRow}>
                    <span className={styles.connectionInfoLabel}>{remoteTypeLabel}:</span>
                    <span className={styles.connectionInfoValue}>
                        <Tooltip text={remoteWebUrl} position="top">
                            <a 
                                href={remoteWebUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className={styles.inlineLink}
                            >
                                {displayName} ↗
                            </a>
                        </Tooltip>
                    </span>
                </div>
                {folderId && (
                    <a
                        href={`/dsa/#folder/${folderId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.inlineLink}
                    >
                        View folder in DSA ↗
                    </a>
                )}
            </div>
            <div className={styles.connectionInfoRow}>
                <span className={styles.connectionInfoLabel}>Remote Contact:</span>
                <span className={styles.connectionInfoValue}>{contactName} ({contactEmail})</span>
            </div>
            <div className={styles.connectionInfoRow}>
                <span className={styles.connectionInfoLabel}>Initiated by:</span>
                <span className={styles.connectionInfoValue}>{initiatedByText}</span>
            </div>
            {requestedDisplay && (
                <div className={styles.connectionInfoRow}>
                    <span className={styles.connectionInfoLabel}>Requested:</span>
                    <span className={styles.connectionInfoValue}>{requestedDisplay}</span>
                </div>
            )}
            {approvedDisplay && (
                <div className={styles.connectionInfoRow}>
                    <span className={styles.connectionInfoLabel}>Approved:</span>
                    <span className={styles.connectionInfoValue}>{approvedDisplay}</span>
                </div>
            )}
            {canceledDisplay && (
                <div className={styles.connectionInfoRow}>
                    <span className={styles.connectionInfoLabel}>Canceled:</span>
                    <span className={styles.connectionInfoValue}>{canceledDisplay}</span>
                </div>
            )}
            {rejectedDisplay && (
                <div className={styles.connectionInfoRow}>
                    <span className={styles.connectionInfoLabel}>Rejected:</span>
                    <span className={styles.connectionInfoValue}>{rejectedDisplay}</span>
                </div>
            )}
            {terminatedDisplay && (
                <div className={styles.connectionInfoRow}>
                    <span className={styles.connectionInfoLabel}>Terminated:</span>
                    <span className={styles.connectionInfoValue}>{terminatedDisplay}</span>
                </div>
            )}
            {connection.terminatedBy && (
                <div className={styles.connectionInfoRow}>
                    <span className={styles.connectionInfoLabel}>Terminated by:</span>
                    <span className={styles.connectionInfoValue}>
                        {connection.terminatedBy.displayName || connection.terminatedBy.name || 'Unknown'} 
                        {connection.terminatedBy.email && ` (${connection.terminatedBy.email})`}
                    </span>
                </div>
            )}
            {/* Display warning for pending connections that would conflict with existing remotes */}
            {conflictingRemote && (connection.status === 'unverified' || connection.status === 'awaiting-approval') && (
                <div className={styles.conflictWarning}>
                    <div className={styles.conflictWarningTitle}>
                        ⚠️ Connection Conflict
                    </div>
                    <div className={styles.conflictWarningText}>
                        A connection already exists for this API URL: <strong>{convertDockerHost(connection.url)}</strong>
                    </div>
                    <div className={styles.conflictWarningText}>
                        Existing connection: <strong>{conflictingRemote.remoteDetails?.displayName || 'Unknown'}</strong>
                    </div>
                    <div className={styles.conflictWarningItalic}>
                        If the existing connection is no longer valid, delete it before approving this request.
                    </div>
                </div>
            )}
            {/* Display error from approve mutation */}
            {duplicateError && (
                <div className={styles.errorMessage}>
                    <div className={styles.errorMessageTitle}>
                        ❌ Approval Failed
                    </div>
                    <div className={styles.errorMessageText}>
                        {errorResponse?.error || 'A connection already exists for this API URL.'}
                    </div>
                    {existingConnection && (
                        <div className={styles.errorMessageText}>
                            Existing connection: <strong>{existingConnection.displayName || 'Unknown'}</strong>
                        </div>
                    )}
                    <div className={styles.errorMessageItalic}>
                        Please delete the existing connection if it is no longer valid, then try approving again.
                    </div>
                </div>
            )}
            {/* Display assetstore conflict error */}
            {assetstoreConflict && (
                <div className={styles.assetstoreConflict}>
                    <div className={styles.assetstoreConflictTitle}>
                        ⚠️ Assetstore Conflict
                    </div>
                    <div className={styles.assetstoreConflictText}>
                        {errorResponse?.error || 'An assetstore already exists for this BDSA UUID.'}
                    </div>
                    {conflictingAssetstore && (
                        <div className={styles.assetstoreConflictText}>
                            Existing assetstore: <strong>{conflictingAssetstore.name || 'Unknown'}</strong>
                        </div>
                    )}
                    <div className={styles.assetstoreConflictActionsText}>
                        Choose an action to resolve this conflict:
                    </div>
                    <div className={styles.assetstoreConflictActions}>
                        <button
                            onClick={() => {
                                approveConnection.mutate({
                                id: connection._id,
                                assetstoreAction: 'reuse',
                                existingAssetstoreId: conflictingAssetstore?.id
                                });
                            }}
                            disabled={isApprovingWithAssetstore}
                            className={`${styles.assetstoreConflictButton} ${styles.reuseButton}`}
                        >
                            {isApprovingWithAssetstore ? 'Processing...' : 'Reuse Existing Assetstore'}
                        </button>
                        <button
                            onClick={() => {
                                approveConnection.mutate({
                                id: connection._id,
                                assetstoreAction: 'delete'
                                });
                            }}
                            disabled={isApprovingWithAssetstore}
                            className={`${styles.assetstoreConflictButton} ${styles.deleteButton}`}
                        >
                            {isApprovingWithAssetstore ? 'Processing...' : 'Delete Old Assetstore'}
                        </button>
                    </div>
                    {approveConnection.error && (
                        <div className={styles.assetstoreError}>
                            Error: {approveConnection.error?.response?.data?.error || approveConnection.error?.message || 'Unknown error'}
                        </div>
                    )}
                </div>
            )}
            {/* Display other approve errors (only for this connection) */}
            {approveError && errorMatchesThisConnection && !duplicateError && !assetstoreConflict && (
                <div className={styles.errorMessage}>
                    <div className={styles.errorMessageTitle}>
                        ❌ Approval Failed
                    </div>
                    <div className={styles.errorMessageText}>
                        {errorResponse?.error || errorResponse?.message || approveError?.message || 'An error occurred while approving the connection.'}
                    </div>
                </div>
            )}
        </div>
    );
}

